import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import tw from "tailwind-styled-components";
import {
    MdSearch, MdKeyboardArrowDown, MdCheck, MdApartment,
} from "react-icons/md";
import { FaRegBuilding } from "react-icons/fa";
const PropertiesFilterDropdown = ({
    properties = [],
    selectedProperty,
    onPropertySelect,
    placeholder = "Select Property",
    className = "",
    showIcon = false,
}) => {

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const filteredProperties = useMemo(() => {
        if (!properties) return [];
        return properties.filter(
            (prop) =>
                prop.name?.toLowerCase().includes(search.toLowerCase())
        );
    }, [properties, search]);

    // Update dropdown position
    const updateDropdownPosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Handle click outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // Also check if click is on the portal dropdown
                const portalDropdown = document.getElementById("portal-properties-dropdown");
                if (portalDropdown && !portalDropdown.contains(event.target)) {
                    setIsOpen(false);
                    setSearch("");
                }
            }
        };

        // Handle escape key
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen]);

    // Update position on scroll and resize
    useEffect(() => {
        if (!isOpen) return;

        updateDropdownPosition();

        const handleUpdate = () => {
            requestAnimationFrame(updateDropdownPosition);
        };

        window.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);

        return () => {
            window.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
        };
    }, [isOpen]);

    const handleSelect = (property) => {
        onPropertySelect(property);
        setIsOpen(false);
        setSearch("");
    };

    const handleToggle = () => {
        if (!isOpen) {
            updateDropdownPosition();
            setSearch("");
        }
        setIsOpen(!isOpen);
    };

    // Dropdown menu content
    const dropdownContent = isOpen && (
        <div
            id="portal-properties-dropdown"
            style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                minWidth: '250px',
                zIndex: 9999
            }}
        >
            <DropdownMenu>
                {/* Search Input */}
                <div className="p-2 border-b">
                    <div className="relative">
                        <MdSearch
                            size={18}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            placeholder="Search properties..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-transparent"
                            autoFocus
                        />
                    </div>
                </div>

                {/* Properties List */}
                <div className="max-h-48 overflow-y-auto">
                    {filteredProperties.length > 0 ? (
                        filteredProperties.map((property) => {
                            return (
                                <PropertiesDropdownItem
                                    key={property.id}
                                    onClick={() => handleSelect(property)}
                                    className={
                                        selectedProperty?.id === property.id
                                            ? "bg-gradient-to-r from-blue-50 to-blue-25 border-l-4 border-l-blue-500 shadow-sm"
                                            : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-25 border-l-4 border-l-transparent"
                                    }
                                >
                                    <div className="flex items-center gap-4 w-full">
                                        <div className="flex-shrink-0 relative">
                                            <div className={`p-2 rounded-lg ${selectedProperty?.id === property.id
                                                ? "bg-blue-100 text-blue-600 shadow-md"
                                                : "bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 shadow-sm"
                                                }`}>
                                                <FaRegBuilding size={20} />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <h3 className="font-semibold text-gray-900 text-sm truncate">
                                                    {property.name}
                                                </h3>
                                                {selectedProperty?.id === property.id && (
                                                    <MdCheck size={16} className="text-blue-600 flex-shrink-0 ml-2" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </PropertiesDropdownItem>
                            )
                        })
                    ) : (
                        <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                            <div className="p-2 bg-gray-100 rounded-lg mb-2">
                                <FaRegBuilding size={24} className="text-gray-400" />
                            </div>
                            <div className="text-sm font-semibold text-gray-500 mb-1">
                                {properties.length === 0 ? "No properties available" : "No matching properties"}
                            </div>
                            <div className="text-xs text-gray-400 max-w-[250px]">
                                {properties.length === 0
                                    ? "Get started by adding your first property"
                                    : "Try searching with different keywords"
                                }
                            </div>
                        </div>
                    )}
                </div>
            </DropdownMenu>
        </div>
    );

    return (
        <div className={className} ref={dropdownRef}>
            <DropdownButton
                ref={buttonRef}
                type="button"
                onClick={handleToggle}
            >
                {/* <span className="truncate text-sm">
                    {selectedProperty?.name || placeholder}
                </span> */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {showIcon && (
                        <FaRegBuilding size={14}
                            className="text-gray-400 flex-shrink-0"
                        />
                    )}

                    <span className="truncate text-sm text-gray-600 font-medium">
                        {selectedProperty?.name || placeholder}
                    </span>
                </div>
                <MdKeyboardArrowDown
                    size={18}
                    className={`transform transition-transform ${isOpen ? "rotate-180" : ""
                        } text-gray-500`}
                />
            </DropdownButton>

            {createPortal(dropdownContent, document.body)}
        </div>
    );
};

// Styled components
const DropdownButton = tw.button`flex items-center justify-between border border-gray-200 rounded-md px-3 bg-white hover:bg-gray-50 hover:border-gray-300 w-full h-10 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent`;
const DropdownMenu = tw.div`bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden`;
const PropertiesDropdownItem = tw.div`flex items-center justify-between px-3 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors`;

export default PropertiesFilterDropdown;